import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import {
  CustomNode,
  NodeParam,
  NodeType,
  createGroupNodeStruct,
} from '@/types/node-types';
import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';
import {
  recordUntouchedDefault,
  resetUntouchedDefaults,
} from '@/stores/helpers/untouched-defaults';
import { CustomEdge } from '@/types/edge-types';
import { useNodeStore } from './node-store';
import {
  Workflow,
  WorkflowDetails,
  NodeStatusEvent,
  WorkflowMetadata,
  SoftwareRepoMode,
  Label,
} from '@/types/workflow-types';
import { getWorkflow } from '@/services/workflow/get-workflow';
import { getWorkflowList } from '@/services/workflow/get-workflow-list';
import { updateWorkflow } from '@/services/workflow/update-workflow';
import { deleteWorkflow } from '@/services/workflow/delete-workflow';
import {
  DuplicateWorkflowResponse,
  duplicateWorkflow as duplicateWorkflowService,
} from '@/services/workflow/duplicate-workflow';
import { useWorkflowPanelStore } from './workflow-panel-store';
import { useWorkflowValidationStore } from './workflow-validation-store';
import { generateWorkflowName } from '@/stores/helpers/workflow-naming';
import { getWorkflowSoftwareRepoPayload } from '@/stores/helpers/workflow-software-repo';
import { useEdgeLayoutStore } from '@/stores/edge-layout-store';
import { expandGroupNodes } from '@/pages/workflow/helper/group-nodes';
import {
  isAccessDeniedError,
  ACCESS_REVOKED_MESSAGE,
} from '@/pages/workflow/helper/is-access-denied-error';

/**
 * Reconcile loaded params against the live catalog so that dropdown options
 * (e.g. the model list) and default values are always up-to-date — even for
 * workflows saved before a new model was added or before options were persisted.
 */
function reconcileParamsWithCatalog(
  params: NodeParam[],
  nodeId: string,
  instanceId: string,
): NodeParam[] {
  const catalogStruct = findCatalogStruct(
    useNodeStore.getState().groupedNodes,
    nodeId,
  );
  if (!catalogStruct) return params;

  const catalogParamMap = new Map(
    catalogStruct.params.map((p) => [p.field_id, p]),
  );

  return params.map((param) => {
    const catalogParam = catalogParamMap.get(param.field_id);
    if (!catalogParam) return param;

    let patched = param;

    // Restore dropdown options from catalog if missing on the saved param
    if (catalogParam.options && !param.options) {
      patched = { ...patched, options: catalogParam.options };
    }

    // Apply catalog default when saved value is empty. This is for display:
    // the save path puts the loaded value back unless the user changed the
    // field (see stores/helpers/untouched-defaults.ts).
    const isEmpty =
      patched.value === undefined ||
      patched.value === null ||
      patched.value === '';
    if (
      isEmpty &&
      catalogParam.default !== undefined &&
      catalogParam.default !== null &&
      catalogParam.default !== ''
    ) {
      recordUntouchedDefault(
        instanceId,
        param.field_id,
        patched.value,
        catalogParam.default,
      );
      patched = { ...patched, value: catalogParam.default };
    }

    return patched;
  });
}

export class WorkflowNotFoundError extends Error {
  constructor(message: string = 'Workflow not found') {
    super(message);
    this.name = 'WorkflowNotFoundError';
  }
}

export interface TemplateMetadata {
  icon?: string;
  color?: string;
  bgColor?: string;
}

export interface SaveWorkflowOptions {
  orgId: string;
  workflowId: string;
  name: string;
  nodes: CustomNode[];
  edges: CustomEdge[];
  description?: string;
  screenshot?: string;
  // Template-specific options
  isTemplate?: boolean;
  templateMetadata?: TemplateMetadata;
  // Whether to update currentWorkflow state (default: true for workflows, false for templates)
  updateCurrentWorkflow?: boolean;
}

type InterventionState = {
  isInterventionPending: boolean;
  interventionRequestId: string | null;
  nodeId: string | null;
  endTime: number | null; // Unix timestamp (ms) when countdown ends
  totalSeconds: number | null;
  nodeIcon?: string;
  nodeIconColor?: string;
  nodeType?: string;
};

interface WorkflowStoreState {
  workflowStatus: 'idle' | 'running' | 'completed' | 'error';
  workflowError: string | null;
  savedWorkflows: WorkflowDetails[];
  templateWorkflows: WorkflowDetails[];
  currentWorkflow: {
    id: string;
    name: string;
    createdAt: string;
    description: string;
    publish_to_template?: boolean;
    publish_to_api?: boolean;
    publish_to_mcp?: boolean;
    software_repo_url?: string | null;
    software_repo_branch?: string | null;
    software_repo_mode?: SoftwareRepoMode | null;
    icon?: string;
    color?: string;
    bg_color?: string;
    created_by?: string;
    created_by_email?: string;
    default_role?: string | null;
    is_locked?: boolean;
    locked_by_id?: string;
    locked_by_name?: string;
    locked_at?: string;
    metadata?: WorkflowMetadata;
    labels?: Label[];
  } | null;
  isWorkflowAdmin: boolean;
  setIsWorkflowAdmin: (isAdmin: boolean) => void;
  isLoadingWorkflows: boolean;
  isLoadingWorkflow: boolean;
  isLoadingTemplate: boolean;
  currentSession: string | null;
  isHistoricalData: boolean;
  lastExecutionTimestamp: string | null;
  loadedNodeStatuses: Record<string, NodeStatusEvent[]> | null;
  interventionState: InterventionState | null;
  setInterventionState: (state: InterventionState | null) => void;
  clearInterventionState: () => void;
  setCurrentSession: (sessionId: string) => void;
  setSavedWorkflows: (workflows: WorkflowDetails[]) => void;
  setTemplateWorkflows: (workflows: WorkflowDetails[]) => void;
  setCurrentWorkflow: (
    workflow: {
      id: string;
      name: string;
      createdAt: string;
      description: string;
      publish_to_template?: boolean;
      publish_to_api?: boolean;
      publish_to_mcp?: boolean;
      software_repo_url?: string | null;
      software_repo_branch?: string | null;
      software_repo_mode?: SoftwareRepoMode | null;
      icon?: string;
      color?: string;
      bg_color?: string;
      created_by?: string;
      created_by_email?: string;
      default_role?: string | null;
      is_locked?: boolean;
      locked_by_id?: string;
      locked_by_name?: string;
      locked_at?: string;
      metadata?: WorkflowMetadata;
    } | null,
  ) => void;
  // Store-only: Save is the persistence boundary, so callers must follow this
  // with one.
  setPublishToApi: (value: boolean) => void;
  saveWorkflow: (options: SaveWorkflowOptions) => Promise<void>;
  loadWorkflow: (workflow: Workflow) => void;
  deleteWorkflow: (id: string, orgId: string) => Promise<void>;
  clearWorkflowState: () => void;
  createBlankWorkflow: () => void;
  loadTemplateWorkflow: (templateId: string, orgId: string) => void;
  getSavedWorkflow: (
    workflowId: string,
    orgId: string,
  ) => Promise<Workflow | null>;
  getWorkflowList: (orgId: string, signal?: AbortSignal) => Promise<void>;
  setIsLoadingWorkflows: (loading: boolean) => void;
  setIsLoadingWorkflow: (loading: boolean) => void;
  setIsLoadingTemplate: (loading: boolean) => void;
  resetLoadingState: () => void;
  setIsHistoricalData: (isHistorical: boolean) => void;
  setLastExecutionTimestamp: (timestamp: string | null) => void;
  /**
   * Replace the loaded-status map for the current historical view.
   *
   * Used by the past-runs sidebar after fetching a past run: it
   * synthesises a minimal terminal event (EXECUTION_SUCCESSFUL or
   * EXECUTION_FAILED) per node_result row and stuffs them in here
   * so that the editor's existing ``isCompleted`` / ``hasError``
   * derivations -- which read from ``nodeStatuses`` -- light up the
   * canvas exactly the same way they would for a live run.
   *
   * The editor's mount-time effect copies this into its local
   * ``nodeStatuses`` state; pass ``null`` (or call
   * ``clearLoadedNodeStatuses``) to drop the historical view.
   */
  setLoadedNodeStatuses: (
    statuses: Record<string, NodeStatusEvent[]> | null,
  ) => void;
  clearLoadedNodeStatuses: () => void;
  restoreCurrentVersion: () => void;
  updateWorkflowMetadata: (
    workflowId: string,
    newName: string,
    newDescription: string | undefined,
    orgId: string,
    isTemplate?: boolean,
    templateMetadata?: TemplateMetadata,
  ) => Promise<void>;
  duplicateWorkflow: (
    workflowId: string,
    orgId: string,
    customName?: string,
    customDescription?: string,
  ) => Promise<string | null>;
  updateWorkflowLabels: (workflowId: string, labels: Label[]) => void;
  // this is to persist node status highlight after workflow run completes
  persistStatusHighlight: boolean;
  setPersistStatusHighlight: (value: boolean) => void;
}

export const useWorkflowStore = create<WorkflowStoreState>((set, _) => ({
  workflowStatus: 'idle',
  workflowError: null,
  savedWorkflows: [],
  templateWorkflows: [],
  currentWorkflow: {
    id: 'new',
    name: '',
    createdAt: new Date().toISOString(),
    description: '',
    icon: '',
    color: '',
    bg_color: '',
    publish_to_template: false,
    publish_to_api: false,
    publish_to_mcp: false,
  },
  isWorkflowAdmin: false,
  setIsWorkflowAdmin: (isAdmin) => set({ isWorkflowAdmin: isAdmin }),
  isLoadingWorkflows: false,
  isLoadingWorkflow: false,
  isLoadingTemplate: false,
  currentSession: null,
  isHistoricalData: false,
  lastExecutionTimestamp: null,
  loadedNodeStatuses: null,
  interventionState: null,
  setInterventionState: (state) => set({ interventionState: state }),
  clearInterventionState: () => set({ interventionState: null }),
  persistStatusHighlight: false,
  setPersistStatusHighlight: (value) => set({ persistStatusHighlight: value }),
  setCurrentSession: (sessionId) => set({ currentSession: sessionId }),
  setSavedWorkflows: (workflows) => {
    set({ savedWorkflows: workflows });
  },
  updateWorkflowLabels: (workflowId, labels) => {
    set((state) => ({
      savedWorkflows: state.savedWorkflows.map((w) =>
        w.workflow_id === workflowId ? { ...w, labels } : w,
      ),
      currentWorkflow:
        state.currentWorkflow?.id === workflowId
          ? { ...state.currentWorkflow, labels }
          : state.currentWorkflow,
    }));
  },
  setTemplateWorkflows: (workflows) => {
    set({ templateWorkflows: workflows });
  },

  setCurrentWorkflow: (workflow) => set({ currentWorkflow: workflow }),
  setPublishToApi: (value) => {
    const { currentWorkflow } = useWorkflowStore.getState();
    if (!currentWorkflow) return;
    set({
      currentWorkflow: { ...currentWorkflow, publish_to_api: value },
    });
  },
  setIsHistoricalData: (isHistorical) =>
    set({ isHistoricalData: isHistorical }),
  setLastExecutionTimestamp: (timestamp) =>
    set({ lastExecutionTimestamp: timestamp }),
  setLoadedNodeStatuses: (statuses) => set({ loadedNodeStatuses: statuses }),
  clearLoadedNodeStatuses: () =>
    set({
      loadedNodeStatuses: null,
      isHistoricalData: false,
      lastExecutionTimestamp: null,
    }),
  restoreCurrentVersion: () => {
    set({
      loadedNodeStatuses: null,
      isHistoricalData: false,
      lastExecutionTimestamp: null,
      interventionState: null,
    });
  },
  saveWorkflow: async (options: SaveWorkflowOptions) => {
    const {
      orgId,
      workflowId,
      name,
      nodes,
      edges,
      description,
      screenshot,
      isTemplate = false,
      templateMetadata,
      updateCurrentWorkflow = !isTemplate, // Default: update for workflows, don't for templates
    } = options;

    if (!name.trim()) {
      throw new Error('Workflow name is required');
    }

    try {
      const currentWorkflow = useWorkflowStore.getState().currentWorkflow;
      const createdAt = currentWorkflow?.createdAt || new Date().toISOString();

      // Extract template metadata if saving as template
      const icon = isTemplate ? templateMetadata?.icon || '' : '';
      const color = isTemplate ? templateMetadata?.color || '' : '';
      const bg_color = isTemplate ? templateMetadata?.bgColor || '' : '';

      const workflowPayload: Workflow = {
        id: workflowId,
        session_id: 'session_id', // TODO: get actual session id
        publish_to_template: isTemplate,
        // Threaded through explicitly: without it the payload defaults to
        // undefined and every Save silently clears the bit.
        publish_to_api: currentWorkflow?.publish_to_api ?? false,
        publish_to_mcp: currentWorkflow?.publish_to_mcp ?? false,
        // Preserve the difference between an absent field on a genuinely new
        // workflow (server applies defaults) and explicit null on an existing
        // workflow (repository attachment remains disabled).
        ...getWorkflowSoftwareRepoPayload(currentWorkflow),
        name: name.trim(),
        nodes,
        edges,
        createdAt,
        description: description?.trim() || '',
        screenshot,
        icon,
        color,
        bg_color,
        metadata: currentWorkflow?.metadata,
      };

      // Save to backend
      const saveResult = await updateWorkflow(workflowPayload, orgId);

      if (saveResult && 'detail' in saveResult && saveResult.detail) {
        throw new Error(saveResult.detail);
      }

      // Reads the just-persisted payload, not currentWorkflow: a stale closure
      // would revert a toggle-then-Save to the pre-toggle value.
      if (updateCurrentWorkflow) {
        set({
          currentWorkflow: {
            id: workflowPayload.id,
            name: workflowPayload.name,
            description: workflowPayload.description || '',
            createdAt: workflowPayload.createdAt || new Date().toISOString(),
            publish_to_template: workflowPayload.publish_to_template,
            publish_to_api: workflowPayload.publish_to_api ?? false,
            publish_to_mcp: workflowPayload.publish_to_mcp ?? false,
            software_repo_url: workflowPayload.software_repo_url ?? null,
            software_repo_branch: workflowPayload.software_repo_branch ?? null,
            software_repo_mode: workflowPayload.software_repo_mode ?? null,
            icon: isTemplate ? icon : '',
            color: isTemplate ? color : '',
            bg_color: isTemplate ? bg_color : '',
            is_locked: currentWorkflow?.is_locked,
            locked_by_id: currentWorkflow?.locked_by_id,
            locked_by_name: currentWorkflow?.locked_by_name,
            locked_at: currentWorkflow?.locked_at,
            metadata: currentWorkflow?.metadata,
            labels: currentWorkflow?.labels,
          },
        });
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      throw error;
    }
  },
  loadWorkflow: (workflow: Workflow) => {
    // Clear existing nodes and edges first to prevent stale data
    const {
      setNodes,
      setEdges,
      setNodeResults,
      setLastSelectedNodeId,
      setGroupContainerFocusedId,
    } = useNodeStore.getState();
    setNodes([]);
    setEdges([]);
    setNodeResults({});
    setLastSelectedNodeId(null);
    // Group ids can collide across loads (e.g. reloading the same workflow,
    // or a duplicated graph) — clear focus so a group in the new graph isn't
    // mistaken for still being focused from the previous canvas.
    setGroupContainerFocusedId(null);
    useWorkflowValidationStore.getState().clearAllErrors();
    // A fresh load starts a fresh record of which defaults the editor filled.
    resetUntouchedDefaults();

    // Always clear execution state first to ensure a fresh load - these will be gone soon
    set({
      loadedNodeStatuses: null,
      isHistoricalData: false,
      lastExecutionTimestamp: null,
      interventionState: null,
    });

    // Small delay to ensure React unmounts old components before mounting new ones
    // This prevents stale memoization when switching between similar workflows
    setTimeout(() => {
      // Load nodes and ensure react_flow_generated_id is synced
      const loadedNodes = workflow.nodes.map((node: CustomNode) => {
        // Sync the react_flow_generated_id with the node's ID
        const updatedData = { ...node.data };

        if (node.type === NodeType.GroupNode && !updatedData.struct) {
          updatedData.struct = createGroupNodeStruct(node.id);
        }

        if (updatedData.struct) {
          updatedData.struct = {
            ...updatedData.struct,
            react_flow_generated_id: node.id,
          };

          // Sync user_config_name to the "name" parameter if it exists
          // user_config_name is the source of truth - it overrides the parameter value
          const userConfigName = updatedData.struct.user_config_name;
          if (userConfigName) {
            updatedData.struct.params = updatedData.struct.params.map(
              (param) => {
                if (param.field_id === 'name') {
                  return { ...param, value: userConfigName };
                }
                return param;
              },
            );
          }

          // Reconcile loaded params with the catalog: fill in missing options
          // and apply default values. Saved workflows may lack dropdown options
          // (e.g. the model list) if they were saved before options were persisted,
          // or if the available options have changed since the workflow was saved.
          updatedData.struct.params = reconcileParamsWithCatalog(
            updatedData.struct.params,
            updatedData.struct.node_id,
            node.id,
          );
        }

        // Convert position values to numbers
        const validPosition = {
          x: Number(node.position?.x) || 0,
          y: Number(node.position?.y) || 0,
        };
        const validMeasurements = {
          width: Number(node.measured?.width) || 0,
          height: Number(node.measured?.height) || 0,
        };

        return {
          ...node,
          position: validPosition,
          measured: validMeasurements,
          selected: false,
          dragging: false,
          data: updatedData,
        };
      });

      // Load edges as simple data, applying the current layout type so the
      // canvas matches the persisted toggle state on initial load.
      const edgeType =
        useEdgeLayoutStore.getState().layout === 'orthogonal'
          ? 'orthogonal'
          : 'default';
      const loadedEdges = workflow.edges.map((edge: CustomEdge) => {
        return {
          ...edge,
          selected: false,
          type: edgeType,
          data: edge.data || {},
        };
      });

      const expandedNodes = expandGroupNodes(loadedNodes);

      useNodeStore.getState().setNodes(expandedNodes);
      useNodeStore.getState().setEdges(loadedEdges);

      // Preserve created_by and created_by_email from existing currentWorkflow
      const existingWorkflow = useWorkflowStore.getState().currentWorkflow;
      set({
        currentWorkflow: {
          id: workflow.id,
          name: workflow.name,
          createdAt: workflow.createdAt || new Date().toISOString(),
          description: workflow.description || '',
          publish_to_template: workflow.publish_to_template,
          publish_to_api: workflow.publish_to_api,
          publish_to_mcp: workflow.publish_to_mcp,
          software_repo_url: workflow.software_repo_url ?? null,
          software_repo_branch: workflow.software_repo_branch ?? null,
          software_repo_mode: workflow.software_repo_mode ?? null,
          icon: workflow.icon,
          color: workflow.color,
          bg_color: workflow.bg_color,
          created_by: existingWorkflow?.created_by,
          created_by_email: existingWorkflow?.created_by_email,
          default_role: workflow.default_role ?? existingWorkflow?.default_role,
          is_locked: existingWorkflow?.is_locked,
          locked_by_id: existingWorkflow?.locked_by_id,
          locked_by_name: existingWorkflow?.locked_by_name,
          locked_at: existingWorkflow?.locked_at,
          metadata: workflow.metadata,
          labels: workflow.labels ?? [],
        },
      });

      // after loading workflow, save it as the first snapshot in history
      const { saveSnapshot, clearHistory } = useNodeStore.getState();
      clearHistory();
      saveSnapshot();
    }, 0); // 0ms delay is enough to break the React update cycle
  },
  deleteWorkflow: async (id: string, orgId: string) => {
    try {
      await deleteWorkflow(id, orgId);

      // Update local state
      set((state) => {
        const updatedWorkflows = state.savedWorkflows.filter(
          (p) => p.workflow_id !== id,
        );
        const updatedTemplates = state.templateWorkflows.filter(
          (p) => p.workflow_id !== id,
        );
        return {
          savedWorkflows: updatedWorkflows,
          templateWorkflows: updatedTemplates,
        };
      });
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  },
  clearWorkflowState: () => {
    const { setNodes, setEdges, setNodeResults, setLastSelectedNodeId } =
      useNodeStore.getState();
    setNodes([]);
    setEdges([]);
    setNodeResults({});
    setLastSelectedNodeId(null);
    useWorkflowValidationStore.getState().clearAllErrors();
    // Clear loaded execution data when clearing workflow
    set({
      loadedNodeStatuses: null,
      isHistoricalData: false,
      lastExecutionTimestamp: null,
      interventionState: null,
    });
  },
  createBlankWorkflow: () => {
    const { setCurrentWorkflow } = useWorkflowStore.getState();
    const { clearWorkflowState } = useWorkflowStore.getState();

    setCurrentWorkflow({
      id: 'new',
      name: generateWorkflowName(),
      description: '',
      createdAt: new Date().toISOString(),
      icon: '',
      color: '',
      bg_color: '',
      publish_to_template: false,
      publish_to_api: false,
      publish_to_mcp: false,
    });
    clearWorkflowState();
  },
  loadTemplateWorkflow: async (templateId: string, orgId: string) => {
    // Generate the workflow name upfront so it's available immediately
    // This prevents showing an empty input while the template loads
    const generatedName = generateWorkflowName();

    set({
      isLoadingTemplate: true,
      currentWorkflow: {
        id: 'new',
        name: generatedName,
        description: '',
        createdAt: new Date().toISOString(),
        icon: '',
        color: '',
        bg_color: '',
        publish_to_template: false,
        publish_to_api: false,
        publish_to_mcp: false,
      },
    });

    try {
      const {
        loadWorkflow,
        createBlankWorkflow,
        getSavedWorkflow,
        // templateWorkflows,
      } = useWorkflowStore.getState();

      // Check if it's a template from db
      // Fetch the full workflow data for the backend template
      try {
        const template = await getSavedWorkflow(templateId, orgId);
        if (template) {
          // Use the same name we generated upfront for consistency
          loadWorkflow({
            ...template,
            id: 'new',
            name: generatedName,
            session_id: 'session_id',
            publish_to_template: false,
            createdAt: new Date().toISOString(),
            labels: [],
          });
        }
        set({ isLoadingTemplate: false });
        return;
      } catch (error) {
        console.error('Error loading backend template:', error);
      }

      // Fallback to blank canvas
      createBlankWorkflow();
    } catch (error) {
      console.error('Error loading template workflow:', error);
      // Fallback to blank canvas on error
      const { createBlankWorkflow } = useWorkflowStore.getState();
      createBlankWorkflow();
    } finally {
      set({ isLoadingTemplate: false });
    }
  },
  getSavedWorkflow: async (workflowId: string, orgId: string) => {
    set({ isLoadingWorkflow: true });
    const { setMcpCode, setMcpEndpoint, clearPublishedCode } =
      useWorkflowPanelStore.getState();

    try {
      clearPublishedCode();
      const response = await getWorkflow(workflowId, orgId);

      if (response?.detail) {
        const detail = response.detail.toLowerCase();
        if (
          detail.includes('not found') ||
          detail.includes('does not exist') ||
          detail.includes('404')
        ) {
          throw new WorkflowNotFoundError(response.detail);
        }
        if (isAccessDeniedError(response.detail)) {
          throw new WorkflowNotFoundError(ACCESS_REVOKED_MESSAGE);
        }
        throw new Error(response.detail);
      }

      if (response?.data?.workflow?.payload) {
        const advancedPayload = response.data.workflow.payload as Workflow;
        const workflowMetadata = response.data.workflow;

        // Restore the persisted MCP connection info (resolved snippet +
        // gateway endpoint written at publish time). No dummy fallback:
        // a published workflow without a persisted snippet predates the
        // gateway wire and would only show a meaningless sample.
        if (advancedPayload.publish_to_mcp && advancedPayload.mcp_code) {
          setMcpCode(advancedPayload.mcp_code);
          setMcpEndpoint(advancedPayload.mcp_endpoint ?? null);
        }

        // Attach default_role and labels from the workflow metadata to the payload
        advancedPayload.default_role = workflowMetadata.default_role ?? null;
        advancedPayload.labels = workflowMetadata.labels ?? [];

        // Set workflow admin flag from response
        if (response.data.is_workflow_admin !== undefined) {
          set({ isWorkflowAdmin: response.data.is_workflow_admin });
        }

        const { currentWorkflow } = useWorkflowStore.getState();
        if (currentWorkflow) {
          set({
            currentWorkflow: {
              ...currentWorkflow,
              created_by: workflowMetadata.created_by,
              created_by_email: workflowMetadata.created_by_email,
              default_role: workflowMetadata.default_role ?? null,
              is_locked: workflowMetadata.is_locked ?? false,
              locked_by_id: workflowMetadata.locked_by_id,
              locked_by_name: workflowMetadata.locked_by_name,
              locked_at: workflowMetadata.locked_at,
              labels: workflowMetadata.labels ?? [],
            },
          });
        }

        return advancedPayload;
      }

      throw new Error('Workflow data not found in response');
    } catch (error) {
      console.error('Error fetching workflow:', error);
      throw error;
    } finally {
      set({ isLoadingWorkflow: false });
    }
  },
  getWorkflowList: async (orgId: string, signal?: AbortSignal) => {
    set({ isLoadingWorkflows: true });

    // Create a timeout signal that aborts after 30 seconds
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, 30000); // 30 second timeout

    // Combine the user signal with the timeout signal
    const combinedSignal = signal
      ? (() => {
          const combinedController = new AbortController();
          const abortHandler = () => combinedController.abort();
          signal.addEventListener('abort', abortHandler);
          timeoutController.signal.addEventListener('abort', abortHandler);
          return combinedController.signal;
        })()
      : timeoutController.signal;

    try {
      const response = await getWorkflowList(orgId, combinedSignal);

      if (response?.data?.workflows) {
        // Transform backend workflow list data directly to Workflow interface
        const allWorkflows: WorkflowDetails[] = response.data.workflows;

        // Separate workflows and templates based on publish_to_template field
        const workflows = allWorkflows.filter((w) => !w.publish_to_template);
        const templates = allWorkflows.filter((w) => w.publish_to_template);

        set({
          savedWorkflows: workflows,
          templateWorkflows: templates,
          isWorkflowAdmin: response.data.is_workflow_admin ?? false,
        });
      } else if (!signal?.aborted) {
        // Error responses surface as { detail } without throwing — keep
        // previously fetched lists so consumers don't lose data
        const error = new Error(
          response?.detail ?? 'Workflow list response missing data',
        );
        console.error('Error fetching workflow list:', error);
        Sentry.captureException(error, { extra: { orgId } });
      }

      // Don't return response, just update state
    } catch (error) {
      // Intentional cancellations (unmount / org change) aren't errors
      if (signal?.aborted) return;
      // Keep previously fetched lists on failure so consumers don't lose data
      console.error('Error fetching workflow list:', error);
      Sentry.captureException(error, { extra: { orgId } });
      // Don't return error response, just log it
    } finally {
      // Clear timeout and always reset loading state
      clearTimeout(timeoutId);
      set({ isLoadingWorkflows: false });
    }
  },
  setIsLoadingWorkflows: (loading) => set({ isLoadingWorkflows: loading }),
  setIsLoadingWorkflow: (loading) => set({ isLoadingWorkflow: loading }),
  setIsLoadingTemplate: (loading) => set({ isLoadingTemplate: loading }),
  resetLoadingState: () =>
    set({
      isLoadingWorkflows: false,
      isLoadingWorkflow: false,
      isLoadingTemplate: false,
    }),

  // Shared utility function for updating workflow metadata in dashboard
  updateWorkflowMetadata: async (
    workflowId: string,
    newName: string,
    newDescription: string | undefined,
    orgId: string,
    isTemplate: boolean = false,
    templateMetadata?: {
      icon?: string;
      color?: string;
      bgColor?: string;
    },
  ) => {
    try {
      // Get the current workflow data from the store
      const { getSavedWorkflow } = useWorkflowStore.getState();
      const workflowResponse = await getSavedWorkflow(workflowId, orgId);

      if (workflowResponse) {
        const workflowData = workflowResponse;

        // Extract template metadata if provided
        const icon = isTemplate ? templateMetadata?.icon || '' : '';
        const color = isTemplate ? templateMetadata?.color || '' : '';
        const bg_color = isTemplate ? templateMetadata?.bgColor || '' : '';

        // Create updated workflow object with new name and description
        const updatedWorkflow = {
          ...workflowData,
          name: newName,
          description: newDescription,
          createdAt: new Date().toISOString(),
          publish_to_template: isTemplate,
          icon,
          color,
          bg_color,
        };

        await updateWorkflow(updatedWorkflow, orgId);
      }
    } catch (error) {
      console.error('Error updating workflow metadata:', error);
      throw error; // Re-throw to let the calling component handle the error display
    }
  },

  // Duplicate workflow function
  duplicateWorkflow: async (
    workflowId: string,
    orgId: string,
    customName?: string,
    customDescription?: string,
  ) => {
    try {
      const { savedWorkflows, currentWorkflow } = useWorkflowStore.getState();

      // Find the workflow being duplicated - check savedWorkflows first, then currentWorkflow
      const workflowToDuplicate =
        savedWorkflows.find((w) => w.workflow_id === workflowId) ||
        currentWorkflow;

      // Use custom name if provided, otherwise generate a unique name
      const duplicatedName = customName || workflowToDuplicate?.name || '';

      // Use custom description if provided, otherwise use original description
      const duplicatedDescription =
        customDescription || workflowToDuplicate?.description || '';

      // Use the new duplicate workflow endpoint
      const response: DuplicateWorkflowResponse =
        await duplicateWorkflowService(
          workflowId,
          orgId,
          duplicatedName,
          duplicatedDescription,
          false, // Not a template
        );

      // Check for error response
      if (response?.detail) {
        throw new Error(response.detail);
      }

      // Return the new workflow ID if successful
      if (response?.data?.workflow?.id) {
        return response.data.workflow.id;
      }

      return null;
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      throw error; // Re-throw to let the calling component handle the error display
    }
  },
}));
